import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsisH, faSort, faSortUp, faSortDown, faTimes, faSearch, faChevronRight } from '@fortawesome/free-solid-svg-icons';

/*
 * Componentes do painel no padrao de ferramentas de admin (Stripe, Polaris,
 * Django admin): cabecalho curto, tabela densa, lista rotulo/valor, menu de
 * acoes e dialogo. Sem enfeites: cor so para estado.
 */

/** Cabecalho da pagina: trilha (no detalhe), titulo, contagem e acoes a direita. */
export function PageHeader({ crumbs, title, meta, actions }) {
  return (
    <header className='ax-header'>
      {crumbs && crumbs.length > 0 && (
        <nav className='ax-crumbs' aria-label='Trilha'>
          {crumbs.map((crumb) => (
            <span key={crumb.label}>
              <Link to={crumb.to}>{crumb.label}</Link>
              <FontAwesomeIcon icon={faChevronRight} aria-hidden='true' />
            </span>
          ))}
        </nav>
      )}
      <div className='ax-header-row'>
        <div className='ax-header-title'>
          <h1>{title}</h1>
          {meta && <span className='ax-header-meta'>{meta}</span>}
        </div>
        {actions && <div className='ax-header-actions'>{actions}</div>}
      </div>
    </header>
  );
}

/** Campo de busca da barra de ferramentas; "/" foca (ver useSlashToSearch). */
export function SearchInput({ id, value, onChange, placeholder, busy }) {
  return (
    <label className='ax-search' htmlFor={id}>
      <FontAwesomeIcon icon={faSearch} aria-hidden='true' />
      <span className='ax-sr'>{placeholder}</span>
      <input id={id} type='search' value={value} placeholder={placeholder} autoComplete='off' onChange={(e) => onChange(e.target.value)} />
      {busy ? <span className='ax-spinner' aria-hidden='true' /> : !value && <kbd className='ax-kbd' aria-hidden='true'>/</kbd>}
      {value && (
        <button type='button' className='ax-search-clear' onClick={() => onChange('')} aria-label='Limpar busca'>
          <FontAwesomeIcon icon={faTimes} />
        </button>
      )}
    </label>
  );
}

/**
 * Tabela: linha inteira clicavel (vai para o detalhe), ordenacao clicando no
 * cabecalho (`sort`/`onSort`), numeros/datas a direita, vazio vira "—".
 * No celular cada linha vira um bloco com o primeiro campo em destaque.
 */
export function DataTable({ columns, rows, rowKey, onRowClick, sort, onSort, loading, empty }) {
  const sortIcon = (column) => {
    if (!column.sortKeys) return null;
    const [asc, desc] = column.sortKeys;
    if (sort === asc) return faSortUp;
    if (sort === desc) return faSortDown;
    return faSort;
  };

  const nextSort = (column) => {
    const [asc, desc] = column.sortKeys;
    return sort === asc ? desc : asc;
  };

  if (loading && rows.length === 0) {
    return (
      <div className='ax-table ax-table-skeleton' aria-busy='true' aria-label='Carregando'>
        {[0, 1, 2, 3, 4].map((i) => <span key={i} />)}
      </div>
    );
  }

  if (!loading && rows.length === 0) return empty || null;

  return (
    <div className={`ax-table${loading ? ' is-loading' : ''}`}>
      <table>
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column.key} scope='col' className={column.align === 'right' ? 'is-right' : ''} style={column.width ? { width: column.width } : undefined}
                aria-sort={column.sortKeys ? (sort === column.sortKeys[0] ? 'ascending' : sort === column.sortKeys[1] ? 'descending' : 'none') : undefined}
              >
                {column.sortKeys && onSort ? (
                  <button type='button' className={`ax-th-sort${column.sortKeys.includes(sort) ? ' is-active' : ''}`} onClick={() => onSort(nextSort(column))}>
                    {column.label}
                    <FontAwesomeIcon icon={sortIcon(column)} aria-hidden='true' />
                  </button>
                ) : column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={rowKey(row)}
              tabIndex={onRowClick ? 0 : undefined}
              className={onRowClick ? 'is-clickable' : ''}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              onKeyDown={onRowClick ? (event) => { if (event.key === 'Enter') onRowClick(row); } : undefined}
            >
              {columns.map((column, index) => {
                const content = column.render(row);
                // Vazio ("—") some no celular, onde a linha vira um bloco de texto.
                const blank = content === '—' || content === null || content === undefined || content === '';
                return (
                  <td key={column.key} className={`${column.align === 'right' ? 'is-right' : ''}${blank ? ' is-blank' : ''}`} data-label={index === 0 ? undefined : column.label}>
                    {blank ? '—' : content}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Lista rotulo/valor (detalhe). `items`: [{ label, value, mono }]; vazio vira "—". */
export function DescList({ items }) {
  return (
    <dl className='ax-dl'>
      {items.filter(Boolean).map((item) => (
        <div key={item.label}>
          <dt>{item.label}</dt>
          <dd className={item.mono ? 'is-mono' : ''}>
            {item.value === null || item.value === undefined || item.value === '' ? <span className='ax-empty-value'>—</span> : item.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/** Painel de secao no detalhe: titulo, acao opcional a direita e conteudo. */
export function Panel({ title, action, children }) {
  return (
    <section className='ax-panel'>
      {(title || action) && (
        <div className='ax-panel-head'>
          {title && <h2>{title}</h2>}
          {action}
        </div>
      )}
      {children}
    </section>
  );
}

/** Menu "⋯" de acoes secundarias. `items`: [{ label, onSelect, danger, disabled, hint }]. */
export function ActionsMenu({ items, label = 'Mais ações' }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const close = (event) => { if (ref.current && !ref.current.contains(event.target)) setOpen(false); };
    const onKey = (event) => { if (event.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const visible = items.filter(Boolean);
  if (!visible.length) return null;

  return (
    <div className='ax-menu' ref={ref}>
      <button type='button' className='ax-btn ax-btn-icon' aria-haspopup='menu' aria-expanded={open} aria-label={label} onClick={() => setOpen((value) => !value)}>
        <FontAwesomeIcon icon={faEllipsisH} />
      </button>
      {open && (
        <ul className='ax-menu-list' role='menu'>
          {visible.map((item) => (
            <li key={item.label} role='none'>
              <button
                type='button'
                role='menuitem'
                className={item.danger ? 'is-danger' : ''}
                disabled={item.disabled}
                onClick={() => { setOpen(false); item.onSelect(); }}
              >
                <span>{item.label}</span>
                {item.hint && <small>{item.hint}</small>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Dialogo modal: titulo, texto, conteudo e rodape com acoes. Esc e fundo fecham. */
export function Dialog({ title, children, footer, onClose }) {
  const ref = useRef(null);

  useEffect(() => {
    const opener = document.activeElement;
    const first = ref.current && ref.current.querySelector('input, textarea, select, button');
    if (first) first.focus();
    const onKey = (event) => { if (event.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      if (opener && typeof opener.focus === 'function' && document.contains(opener)) opener.focus();
    };
  }, [onClose]);

  return createPortal(
    <div className='ax-dialog-backdrop' onMouseDown={onClose}>
      <div className='ax-dialog' role='dialog' aria-modal='true' aria-labelledby='ax-dialog-title' ref={ref} onMouseDown={(e) => e.stopPropagation()}>
        <h2 id='ax-dialog-title'>{title}</h2>
        <div className='ax-dialog-body'>{children}</div>
        {footer && <div className='ax-dialog-footer'>{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}

/** Estado vazio: o que aconteceu e o que fazer. */
export function EmptyState({ title, text, action }) {
  return (
    <div className='ax-empty'>
      <strong>{title}</strong>
      {text && <p>{text}</p>}
      {action}
    </div>
  );
}
